/* eslint-disable camelcase */
import React, { useState } from "react";
import { FormattedMessage, injectIntl } from "react-intl";
import {
  Row,
  Column,
  Container,
  Loader,
  Heading,
  ActionBar,
  Details,
  DetailsItem,
  PictureGallery,
  DetailsHeaderItem,
  DetailsPicture,
  ActionBarButtons,
  EditActionButton,
  withApi,
  withUser,
  withAccessRights,
  apiShape,
  accessRightsShape,
  intlShape,
  withNotifier,
  notifierShape,
  parseISO8601Date,
} from "lcm-iot-commons";

import {
  networkStatusIconClassName,
  networkInterfaceIconClassName,
} from "../../utils/statusUtils";

export function extractInitialValues(
  response: $TSFixMe,
  accessRights: $TSFixMe,
  match: $TSFixMe,
  intl: $TSFixMe,
) {
  const initialValues = { ...response };
  initialValues.modus = intl.formatMessage({
    id: `enum.network_interface_modus.${response.modus.toLowerCase()}`,
  });
  initialValues.status = intl.formatMessage({
    id: `enum.network_interface_status.${response.status}`,
  });
  initialValues.http_proxy_auth_value =
    response.http_proxy_auth === false
      ? intl.formatMessage({ id: "enum.network_interface_status.inactive" })
      : intl.formatMessage({ id: "enum.network_interface_status.active" });
  initialValues.http_proxy_host = response.http_proxy_host || "-";
  initialValues.http_proxy_port = response.http_proxy_port || "-";
  initialValues.editable = accessRights.canUpdate && response.type === "LAN";
  initialValues.editEndpoint = `/edge_devices/${match.params?.id}/network_interfaces/${match.params?.networkInterfaceId}/edit`;
  initialValues.hasProxy = Boolean(
    response.http_proxy_host || response.http_proxy_port,
  );
  initialValues.isManualMode = response.modus === "manual";
  initialValues.icon = networkStatusIconClassName(response.status);
  initialValues.placeholder = networkInterfaceIconClassName();
  initialValues.apply_timestamp = response.apply_timestamp
    ? parseISO8601Date(response.apply_timestamp)
    : null;
  initialValues.type =
    response.number !== undefined
      ? `${response.type} ${response.number}`
      : response.type;
  initialValues.isWan = response.type === "WAN";
  initialValues.showDefaultGateway = initialValues.isWan;
  initialValues.showDns = initialValues.isWan;
  initialValues.showProxySettings = initialValues.isWan;
  return initialValues;
}

export const validateAccessRights = (accessRights: $TSFixMe) =>
  accessRights.canRead;

type NetworkInterfaceDetailsProps = {
  api: apiShape;
  intl: intlShape;
  accessRights: accessRightsShape;
  notifier: notifierShape;
  match: {
    params?: {
      id?: string;
      networkInterfaceId?: string;
    };
  };
};

type NetworkInterfaceDetailsState = {
  dns1?: string;
  dns2?: string;
  name?: string;
  type?: string;
  status?: string;
  modus?: string;
  editable?: boolean;
  editEndpoint?: string;
  icon?: string;
  placeholder?: string;
  http_proxy_host?: string;
  http_proxy_port?: string | number;
  http_proxy_auth_value?: string;
  ip_address?: string;
  subnet_mask?: string;
  default_gateway?: string;
  hasProxy?: boolean;
  isManualMode?: boolean;
  showDefaultGateway?: boolean;
  showDns?: boolean;
  showProxySettings?: boolean;
};

export function NetworkInterfaceDetails(props: NetworkInterfaceDetailsProps) {
  const { api, accessRights, match, intl, notifier } = props;
  const { id, networkInterfaceId } = match.params || {};
  const [networkInterface, setNetworkInterface] =
    useState<NetworkInterfaceDetailsState | null>(null);

  const loadData = async () => {
    try {
      const response = await api.get(
        `/edm/edge_devices/${id}/network_interfaces/${networkInterfaceId}`,
      );
      setNetworkInterface(
        extractInitialValues(response, accessRights, match, intl),
      );
    } catch (error) {
      notifier.showError(api.translateError(error));
      setNetworkInterface({});
    }
  };
  React.useEffect(() => {
    loadData();
  }, [id]);

  const {
    dns1,
    dns2,
    name,
    type,
    status,
    modus,
    editable,
    editEndpoint,
    icon,
    placeholder,
    http_proxy_host,
    http_proxy_port,
    http_proxy_auth_value,
    ip_address,
    subnet_mask,
    default_gateway,
    hasProxy,
    showDefaultGateway,
    showDns,
    showProxySettings,
  } = networkInterface || {};

  return (
    <Container>
      <Row>
        <Column>
          <ActionBar>
            <Heading
              id="details-header"
              title={intl.formatMessage({
                id: "network_interface_details.header",
              })}
            />
            <ActionBarButtons>
              <EditActionButton
                id="edit-network-interface-button"
                disabled={!editable}
                target={editEndpoint}
              />
            </ActionBarButtons>
          </ActionBar>
          {networkInterface && (
            <Details id="network-interface-item">
              <DetailsPicture>
                <PictureGallery placeholder={placeholder} />
              </DetailsPicture>
              <DetailsHeaderItem
                id="network-interface-name"
                translationKey="label.name"
                value={name}
              />
              <DetailsHeaderItem
                id="network-interface-type"
                translationKey="label.type"
                value={type}
              />
              <DetailsHeaderItem
                id="network-interface-modus"
                translationKey="label.modus"
                value={modus}
              />
              <DetailsItem
                id="network-interface-status"
                icon={icon}
                translationKey="label.status"
                value={status}
              />
              <Details id="network-interface-manual-mode">
                <h2>
                  <FormattedMessage id="network_interface_details.ip_configuration" />
                </h2>
                <DetailsItem
                  id="network-interface-ip-address"
                  translationKey="label.ip_address"
                  value={ip_address}
                />
                <DetailsItem
                  id="network-interface-subnet_mask"
                  translationKey="label.subnet_mask"
                  value={subnet_mask}
                />
                {showDefaultGateway && (
                  <DetailsItem
                    id="network-interface-default-gateway"
                    translationKey="label.default_gateway"
                    value={default_gateway}
                  />
                )}
                {showDns && (
                  <>
                    <DetailsItem
                      id="network-interface-dns1"
                      translationKey="label.dns1"
                      value={dns1}
                    />
                    <DetailsItem
                      id="network-interface-dns2"
                      translationKey="label.dns2"
                      value={dns2}
                    />
                  </>
                )}
              </Details>
              {showProxySettings && hasProxy && (
                <Details id="network-interface-proxy-settings">
                  <h2>
                    <FormattedMessage id="network_interface_details.proxy" />
                  </h2>
                  <DetailsItem
                    id="network-interface-http-proxy-host"
                    translationKey="label.http_proxy_host"
                    value={http_proxy_host}
                  />
                  <DetailsItem
                    id="network-interface-http-proxy-port"
                    translationKey="label.http_proxy_port"
                    value={`${http_proxy_port}`}
                  />
                  <DetailsItem
                    id="network-interface-http-proxy-auth"
                    translationKey="label.http_proxy_auth"
                    value={http_proxy_auth_value}
                  />
                </Details>
              )}
            </Details>
          )}
          <Loader id="loader" loading={!networkInterface} />
        </Column>
      </Row>
    </Container>
  );
}

export default withNotifier(
  withApi(
    withUser(
      // @ts-expect-error TS(2345) FIXME: Argument of type 'FC<WithIntlProps<Readonly<Omit<N... Remove this comment to see the full error message
      injectIntl(
        withAccessRights(
          NetworkInterfaceDetails,
          "EDM::EdgeDevice",
          validateAccessRights,
        ),
      ),
    ),
  ),
);
